import { LocalAtRecord } from '../../../../db';
import { JobsFieldSet } from '@rogoag/airtable/crm';

/**
 * Domain model for heatmap attributes following DDD principles
 */

export type HeatmapAttribute = 'daysLate' | 'boundaryAcres';

export interface HeatmapAttributeConfig {
  label: string;
  description: string;
  calculate: (job: LocalAtRecord<JobsFieldSet>) => number | undefined;
}

/**
 * Value object representing a heatmap data point
 */
export class HeatmapDataPoint {
  constructor(
    public readonly coordinates: [number, number],
    public readonly weight: number,
    public readonly jobId: string,
    public readonly metadata: Record<string, any>,
  ) {
    if (weight < 0 || weight > 1) {
      throw new Error('Weight must be between 0 and 1');
    }
  }
}

/**
 * Domain service for calculating attribute values from jobs
 */
export class HeatmapAttributeCalculator {
  private static readonly ATTRIBUTES: Record<HeatmapAttribute, HeatmapAttributeConfig> = {
    daysLate: {
      label: 'Days Late',
      description: 'Number of days between scheduled date and sample date',
      calculate: (job: LocalAtRecord<JobsFieldSet>): number | undefined => {
        return job.get('Late (days)') || undefined;
      },
    },
    boundaryAcres: {
      label: 'Boundary Acres',
      description: 'Size of the field boundary in acres',
      calculate: (job: LocalAtRecord<JobsFieldSet>): number => {
        const acres = job.get('Boundary Acres');

        return parseFloat(acres?.toString() || '0');
      },
    },
  };

  static getConfig(attribute: HeatmapAttribute): HeatmapAttributeConfig {
    return this.ATTRIBUTES[attribute];
  }

  static getAllAttributes(): HeatmapAttribute[] {
    return Object.keys(this.ATTRIBUTES) as HeatmapAttribute[];
  }

  static calculateValue(job: LocalAtRecord<JobsFieldSet>, attribute: HeatmapAttribute): number | undefined {
    const config = this.getConfig(attribute);

    return config.calculate(job);
  }

  /**
   * Calculate percentile value from sorted array
   */
  static calculatePercentile(sortedValues: number[], percentile: number): number {
    const index = (percentile / 100) * (sortedValues.length - 1);
    const lower = Math.floor(index);
    const upper = Math.ceil(index);
    const weight = index - lower;

    if (lower === upper) {
      return sortedValues[lower];
    }

    return sortedValues[lower] * (1 - weight) + sortedValues[upper] * weight;
  }

  /**
   * Normalize values to 0-1 range using percentile-based normalization
   * This handles skewed distributions better than linear normalization
   */
  static normalizeValues(values: number[]): number[] {
    if (values.length === 0) {
      return [];
    }

    // Sort values for percentile calculation
    const sortedValues = [...values].sort((a, b) => a - b);

    const maxValue = sortedValues[sortedValues.length - 1];
    const minValue = sortedValues[0];

    // If all values are the same, return array of 0.5
    if (maxValue === minValue) {
      return values.map(() => 0.5);
    }

    // Use percentile-based normalization to handle outliers
    // Calculate 5th and 95th percentiles to clip extreme outliers
    const p5 = this.calculatePercentile(sortedValues, 5);
    const p95 = this.calculatePercentile(sortedValues, 95);

    // If the percentile range is too small, fall back to min/max
    const usePercentiles = p95 - p5 > 0;
    const lowerBound = usePercentiles ? p5 : minValue;
    const upperBound = usePercentiles ? p95 : maxValue;
    const range = upperBound - lowerBound;

    // Normalize to 0-1 range, clipping outliers
    return values.map((value) => {
      // Clip value to percentile bounds
      const clippedValue = Math.max(lowerBound, Math.min(upperBound, value));

      return (clippedValue - lowerBound) / range;
    });
  }

  /**
   * Calculate percentile-based bin edges for more even distribution
   */
  static calculateBinEdges(values: number[], numBins: number): number[] {
    if (values.length === 0) {
      return [];
    }

    const sortedValues = [...values].sort((a, b) => a - b);
    const edges: number[] = [sortedValues[0]];

    // Calculate percentile for each bin edge
    for (let i = 1; i < numBins; i++) {
      const percentile = (i / numBins) * 100;
      edges.push(this.calculatePercentile(sortedValues, percentile));
    }

    edges.push(sortedValues[sortedValues.length - 1]);

    return edges;
  }

  /**
   * Create heatmap data points from jobs
   */
  static createDataPoints(
    jobs: LocalAtRecord<JobsFieldSet>[],
    attribute: HeatmapAttribute,
    getPullinFromJob: (job: LocalAtRecord<JobsFieldSet>) => [number, number] | undefined,
  ): HeatmapDataPoint[] {
    // Calculate all values first
    const jobsWithValues = jobs
      .map((job) => ({
        job,
        value: this.calculateValue(job, attribute),
        coordinates: getPullinFromJob(job),
      }))
      .filter((item) => item.coordinates !== undefined && item.value !== undefined);

    if (jobsWithValues.length === 0) {
      return [];
    }

    // Normalize weights
    const values = jobsWithValues.map((item) => item.value) as number[];
    const normalizedWeights = this.normalizeValues(values);

    // Create data points
    return jobsWithValues.map((item, index) => {
      return new HeatmapDataPoint(item.coordinates!, normalizedWeights[index], item.job.id, {
        name: item.job.get('Name'),
        rawValue: item.value,
        attribute,
      });
    });
  }
}
